/*
 * A dose this practice did not give (KIN-1883).
 *
 * The record is an ordinary administration in every respect but one: we did not perform it,
 * so it is never reported to NIIS and never claimed from PCRS. `administeredElsewhereAt`
 * being non-null is the whole marker — the backend keys every one of its guarantees off
 * that, and so does everything here.
 *
 * It is additive to a normal record, never a replacement for one. In particular it keeps
 * its product: the schedule counts doses by product, so a record without one fills no slot
 * and the child stays on the recall list for a vaccine they have already had — the exact
 * false work this feature exists to remove.
 */

export const isAdministeredElsewhere = (immunisation) =>
  !!immunisation?.administeredElsewhereAt;

/*
 * The label wherever doses are listed. A clinician has to be able to tell at a glance what
 * this practice is answerable for: a dose we gave and a dose we were told about are
 * different kinds of fact, and showing them identically invites someone to treat hearsay as
 * our own record.
 *
 * `administeredElsewhereBy` is free text by design, so it can be blank — the label still has
 * to say the thing that matters, which is that it was not us.
 */
export const formatAdministeredElsewhere = (immunisation) => {
  if (!isAdministeredElsewhere(immunisation)) return null;

  const source = immunisation.administeredElsewhereBy?.trim();

  return source ? `Given elsewhere — ${source}` : "Given elsewhere";
};

/*
 * Meaningless for someone else's dose, and demanding them is what makes the ordinary path
 * unusable for this: nobody transcribing a parent's account of a dose given in another
 * country has the batch number or the site it went into.
 *
 * Deliberately absent, because both stay required: the product, which is what fills the
 * schedule slot, and the date, which drives the interval rules.
 */
export const FIELDS_NOT_REQUIRED_WHEN_GIVEN_ELSEWHERE = [
  "batchNo",
  "expiryDate",
  "siteGiven",
  "administrationMethod",
  "vaccinator",
];

export const isFieldRequiredWhenGivenElsewhere = (field) =>
  !FIELDS_NOT_REQUIRED_WHEN_GIVEN_ELSEWHERE.includes(field);

/*
 * The two wire fields, from what the form holds.
 *
 * The timestamp is the marker rather than a separate boolean, so it has to carry a real
 * value. It records when the dose was given as far as anyone knows — the same date the
 * clinician entered — not when the row was typed, which would be a different fact and a
 * misleading one on a dose from 2019.
 */
export const buildAdministeredElsewhereFields = ({
  isGivenElsewhere,
  givenElsewhereBy,
  dateGiven,
}) => {
  if (!isGivenElsewhere || !dateGiven) return {};

  const source = givenElsewhereBy?.trim();

  return {
    administeredElsewhereAt: `${dateGiven}T00:00:00`,
    ...(source ? { administeredElsewhereBy: source } : {}),
  };
};
